import { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import AppContext from '../../services/context';
import FeedbackModal from './FeedbackModal';
import WhatsappConsentModal from './WhatsappConsentModal';
import TermsAcceptanceGate from './TermsAcceptanceGate';
import { CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION } from '../../config/legal';
import { trackEvent } from '../../lib/analytics';

// Estados do orquestrador:
// 'idle' | 'consent-whatsapp' | 'gate-terms' | 'compose'
const FeedbackButton = () => {
  const { userState, userActions, themeKey } = useContext(AppContext);
  const isDarkTheme = themeKey === 'dark';
  const [stage, setStage] = useState('idle');

  const { profile, isAuthenticated, hasWhatsapp } = userState;
  const termsOk = profile?.termsAcceptedVersion === CURRENT_TERMS_VERSION
    && profile?.privacyAcceptedVersion === CURRENT_PRIVACY_VERSION;

  const open = () => {
    if (!isAuthenticated) return;
    const nextStage = !hasWhatsapp ? 'consent-whatsapp' : !termsOk ? 'gate-terms' : 'compose';
    trackEvent('feedback_open', {
      flow_name: 'feedback',
      step_name: nextStage,
      has_whatsapp: hasWhatsapp,
      legal_current: termsOk,
    });
    setStage(nextStage);
  };

  // Após avançar etapas, dispara refetch para refletir mudanças e avança o fluxo.
  const advanceAfterConsent = async () => {
    await userActions.refetch?.();
    setStage(termsOk ? 'compose' : 'gate-terms');
  };

  const advanceAfterTerms = async () => {
    await userActions.refetch?.();
    setStage('compose');
  };

  useEffect(() => {
    // Se enquanto a modal está aberta o usuário ficou consistente, segue em frente automaticamente.
    if (stage === 'consent-whatsapp' && hasWhatsapp) setStage(termsOk ? 'compose' : 'gate-terms');
    if (stage === 'gate-terms' && termsOk) setStage('compose');
  }, [hasWhatsapp, termsOk, stage]);

  if (!isAuthenticated) return null;

  return (
    <>
      <button
        type="button"
        onClick={open}
        className={clsx(
          'fixed bottom-6 right-6 z-40 px-4 py-3 rounded-full shadow-lg font-semibold flex items-center gap-2 transition-colors',
          isDarkTheme
            ? 'bg-greenbook-dark-primary text-greenbook-dark-toggle-text hover:bg-greenbook-light-green'
            : 'bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900'
        )}
        aria-label="Enviar feedback"
      >
        💬 Feedback
      </button>

      <WhatsappConsentModal
        isOpen={stage === 'consent-whatsapp'}
        initialValue={profile?.whatsapp || profile?.phone || ''}
        onClose={() => setStage('idle')}
        onSaved={advanceAfterConsent}
        isDarkTheme={isDarkTheme}
      />

      <TermsAcceptanceGate
        isOpen={stage === 'gate-terms'}
        onClose={() => setStage('idle')}
        onAccepted={advanceAfterTerms}
        isDarkTheme={isDarkTheme}
      />

      <FeedbackModal
        isOpen={stage === 'compose'}
        onClose={() => setStage('idle')}
        isDarkTheme={isDarkTheme}
        profile={profile}
      />
    </>
  );
};

export default FeedbackButton;
